/**
 * Server state for build step 10 — the Money hub and the Zoho sync (packages/contracts routes.step10, all 11):
 *   GET /money/overview, GET /money/usage-vs-billing, GET /money/flags, POST /money/flags/{id}/escalate|task|ack,
 *   GET /integrations, POST /integrations/zoho/sync, GET /integrations/zoho/invoice-lines,
 *   POST /integrations/zoho/invoice-lines/{id}/map, GET /jobs/runs.
 * Founder and associate director only (revenue.read); the server answers 403 to anyone else.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AckFlagBody, EscalateFlagBody, FlagTaskBody, RouteQuery, Severity } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';
import { allPages, refreshAfterWrite } from './work';

export const mkeys = {
  all: ['money'] as const,
  overview: ['money', 'overview'] as const,
  usage: ['money', 'usage'] as const,
  flags: (state: string, severity: string) => ['money', 'flags', state, severity] as const,
  integrations: ['money', 'integrations'] as const,
  lines: ['money', 'invoice-lines'] as const,
  runs: ['money', 'runs'] as const,
};

const refresh = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: mkeys.all });
  refreshAfterWrite(qc);
};

/** `enabled: false` skips the call (Today asks only on the Zoho branch, and only with money.read). */
export const useMoneyOverview = (enabled = true) =>
  useQuery({ queryKey: mkeys.overview, queryFn: () => call('money.overview', { query: {} }), enabled });

/** Every client page (the hub needs the whole list to filter by product and count). */
export function useUsageVsBilling(enabled = true) {
  return useQuery({
    queryKey: mkeys.usage,
    enabled,
    queryFn: async () => {
      const first = await call('money.usageVsBilling', { query: { limit: 200 } as RouteQuery<'money.usageVsBilling'> });
      let rows = first.data;
      let cursor = first.next_cursor;
      while (cursor) {
        const p = await call('money.usageVsBilling', { query: { limit: 200, cursor } as RouteQuery<'money.usageVsBilling'> });
        rows = rows.concat(p.data);
        cursor = p.next_cursor;
      }
      return { ...first, data: rows };
    },
  });
}

export function useFlagHits(state: 'open' | 'acknowledged', severity: Severity | 'all') {
  return useQuery({
    queryKey: mkeys.flags(state, severity),
    queryFn: async () => {
      const q = { state, limit: 200, ...(severity === 'all' ? {} : { severity }) };
      const first = await call('money.flags', { query: q as RouteQuery<'money.flags'> });
      let rows = first.data;
      let cursor = first.next_cursor;
      while (cursor) {
        const p = await call('money.flags', { query: { ...q, cursor } as RouteQuery<'money.flags'> });
        rows = rows.concat(p.data);
        cursor = p.next_cursor;
      }
      return { ...first, data: rows };
    },
  });
}

export const useIntegrations = (enabled = true) =>
  useQuery({ queryKey: mkeys.integrations, queryFn: () => call('integrations.list'), enabled, refetchInterval: 30_000 });

export const useUnmappedLines = (enabled = true) =>
  useQuery({
    queryKey: mkeys.lines,
    queryFn: () =>
      allPages((cursor) => call('integrations.zohoInvoiceLines', { query: { mapped: 'false', limit: 200, cursor } as RouteQuery<'integrations.zohoInvoiceLines'> })),
    enabled,
  });

export function useZohoRuns(enabled = true) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: mkeys.runs,
    queryFn: () => call('jobs.runs', { query: { job: 'zoho.sync', limit: 5 } as RouteQuery<'jobs.runs'> }),
    enabled,
    refetchInterval: (q) => (q.state.data?.data.some((r) => r.status === 'running') ? 3000 : false),
  });
  // When a run that was running has finished (ok or failed), the unmapped lines, freshness and overview it
  // changed are refetched now rather than on the next 30-second poll or refocus.
  const wasRunning = useRef<Set<string>>(new Set());
  useEffect(() => {
    const runs = q.data?.data ?? [];
    const finished = runs.some((r) => r.status !== 'running' && wasRunning.current.has(r.id));
    wasRunning.current = new Set(runs.filter((r) => r.status === 'running').map((r) => r.id));
    if (finished) void qc.invalidateQueries({ queryKey: mkeys.all, predicate: (x) => x.queryKey[1] !== 'runs' });
  }, [q.data, qc]);
  return q;
}

function useMoneyWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => refresh(qc), onError: () => refresh(qc) });
}

export const useFlagAck = () => useMoneyWrite((v: { id: string; body: AckFlagBody }) => call('money.flagAck', { params: { id: v.id }, body: v.body }));
export const useFlagEscalate = () =>
  useMoneyWrite((v: { id: string; body: EscalateFlagBody }) =>
    call('money.flagEscalate', { params: { id: v.id }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );
export const useFlagTask = () =>
  useMoneyWrite((v: { id: string; body: FlagTaskBody }) => call('money.flagTask', { params: { id: v.id }, body: v.body, idempotencyKey: newIdempotencyKey() }));
export const useZohoSync = () => useMoneyWrite((_: void) => call('integrations.zohoSync'));
export const useMapLine = () =>
  useMoneyWrite((v: { id: string; product_key: string }) => call('integrations.zohoMapLine', { params: { id: v.id }, body: { product_key: v.product_key } }));

/** Prototype `inr`: "₹7.3 L" — one decimal, lakh. */
export const inr = (lakh: number): string => `₹${(Math.round(lakh * 10) / 10).toFixed(1)} L`;

/** "6:02 am" today, else "15 Oct, 6:02 am". */
export function syncedAt(iso: string): string {
  const d = new Date(iso);
  const t = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).toLowerCase();
  const sameDay = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) === new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  return sameDay ? t : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}, ${t}`;
}
